import { useState, useEffect } from 'react';
import { usePreferences, useUpdatePreferences, useLLMProfiles, useCreateLLMProfile, useUpdateLLMProfile, useDeleteLLMProfile, useUnprocessedCount, useReprocessUnprocessed } from '@/hooks/useSettings';
import { useManualRun } from '@/hooks/useProcessing';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Loader2, Plus, Trash2, Play, Pencil, RefreshCw, AlertCircle, FileCode } from 'lucide-react';

import type { LLMProfile } from '@/types';
import { useUIStore } from '@/store';
import { LLMProfilesYAML } from '@/components/settings';

function PreferencesTab() {
  const { data: preferences, isLoading } = usePreferences();
  const updateMutation = useUpdatePreferences();
  
  const [formData, setFormData] = useState({
    target_language: 'zh-CN',
    timezone: 'Asia/Shanghai',
    processing_times: ['09:00', '18:00'],
    freshness_window_hours: 48,
    tweets_per_page: 20,
    auto_translate: true,
    default_expand_feed: false,
    search_provider: 'tavily',
  });
  
  // Update form data when preferences are loaded
  useEffect(() => {
    if (preferences) {
      setFormData({
        target_language: preferences.target_language || 'zh-CN',
        timezone: preferences.timezone || 'Asia/Shanghai',
        processing_times: preferences.processing_times || ['09:00', '18:00'],
        freshness_window_hours: preferences.freshness_window_hours || 48,
        tweets_per_page: preferences.tweets_per_page || 20,
        auto_translate: preferences.auto_translate === 1,
        default_expand_feed: preferences.default_expand_feed === 1,
        search_provider: preferences.search_provider || 'tavily',
      });
    }
  }, [preferences]);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({
      ...formData,
      auto_translate: formData.auto_translate ? 1 : 0,
      default_expand_feed: formData.default_expand_feed ? 1 : 0,
    });
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm font-medium">目标语言</label>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
            value={formData.target_language}
            onChange={(e) => setFormData({ ...formData, target_language: e.target.value })}
          >
            <option value="zh-CN">简体中文</option>
            <option value="zh-TW">繁體中文</option>
            <option value="en">English</option>
            <option value="ja">日本語</option>
            <option value="ko">한국어</option>
          </select>
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">时区</label>
          <Input
            value={formData.timezone}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
          />
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">处理时间（逗号分隔）</label>
          <Input
            value={formData.processing_times.join(', ')}
            onChange={(e) => setFormData({
              ...formData,
              processing_times: e.target.value.split(',').map(t => t.trim())
            })}
            placeholder="09:00, 18:00"
          />
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">新鲜度窗口（小时）</label>
          <Input
            type="number"
            value={formData.freshness_window_hours}
            onChange={(e) => setFormData({ ...formData, freshness_window_hours: parseInt(e.target.value) })}
            min={1}
            max={168}
          />
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">每页推文数</label>
          <Input
            type="number"
            value={formData.tweets_per_page}
            onChange={(e) => setFormData({ ...formData, tweets_per_page: parseInt(e.target.value) })}
            min={10}
            max={100}
          />
        </div>
        
        <div className="space-y-2">
          <label className="text-sm font-medium">搜索提供商</label>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
            value={formData.search_provider}
            onChange={(e) => setFormData({ ...formData, search_provider: e.target.value })}
          >
            <option value="tavily">Tavily</option>
            <option value="llm_search">LLM Search</option>
            <option value="hybrid">混合</option>
          </select>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="auto_translate"
          checked={formData.auto_translate}
          onChange={(e) => setFormData({ ...formData, auto_translate: e.target.checked })}
          className="h-4 w-4"
        />
        <label htmlFor="auto_translate" className="text-sm">自动翻译推文</label>
      </div>
      
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="default_expand_feed"
          checked={formData.default_expand_feed}
          onChange={(e) => setFormData({ ...formData, default_expand_feed: e.target.checked })}
          className="h-4 w-4"
        />
        <label htmlFor="default_expand_feed" className="text-sm">默认展开信息流（无需点击即可查看全文）</label>
      </div>
      
      <Button
        type="submit"
        disabled={updateMutation.isPending}
      >
        {updateMutation.isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            保存中...
          </>
        ) : (
          '保存设置'
        )}
      </Button>
    </form>
  );
}

function LLMProfilesTab() {
  const { data: profiles, isLoading } = useLLMProfiles();
  const createMutation = useCreateLLMProfile();
  const updateMutation = useUpdateLLMProfile();
  const deleteMutation = useDeleteLLMProfile();
  const { data: unprocessedCount, isLoading: isLoadingCount } = useUnprocessedCount();
  const reprocessMutation = useReprocessUnprocessed();
  const { addToast } = useUIStore();
  const [showForm, setShowForm] = useState(false);
  
  const handleReprocess = () => {
    reprocessMutation.mutate(undefined, {
      onSuccess: (data) => {
        addToast({
          title: '重新处理已启动',
          description: data.message,
        });
      },
      onError: (error) => {
        addToast({
          title: '处理失败',
          description: error instanceof Error ? error.message : '未知错误',
          variant: 'destructive',
        });
      },
    });
  };
  const [editingProfile, setEditingProfile] = useState<LLMProfile | null>(null);
  const [formData, setFormData] = useState({
    purpose: 'translator',
    name: '',
    base_url: 'https://api.openai.com/v1',
    api_key: '',
    model: 'gpt-4o-mini',
    temperature: 0.7,
    max_tokens: 4096,
    top_p: 1.0,
  });
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  const resetForm = () => {
    setFormData({
      purpose: 'translator',
      name: '',
      base_url: 'https://api.openai.com/v1',
      api_key: '',
      model: 'gpt-4o-mini',
      temperature: 0.7,
      max_tokens: 4096,
      top_p: 1.0,
    });
    setEditingProfile(null);
  };
  
  const handleOpenCreate = () => {
    resetForm();
    setShowForm(true);
  };
  
  const handleOpenEdit = (profile: LLMProfile) => {
    setEditingProfile(profile);
    setFormData({
      purpose: profile.purpose,
      name: profile.name,
      base_url: '',
      api_key: '',
      model: profile.model,
      temperature: profile.temperature,
      max_tokens: profile.max_tokens,
      top_p: profile.top_p,
    });
    setShowForm(true);
  };
  
  const handleCancel = () => {
    setShowForm(false);
    resetForm();
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingProfile) {
      // Update existing profile
      const updateData: Record<string, string | number> = {};
      if (formData.name !== editingProfile.name) updateData.name = formData.name;
      if (formData.purpose !== editingProfile.purpose) updateData.purpose = formData.purpose;
      if (formData.model !== editingProfile.model) updateData.model = formData.model;
      if (formData.temperature !== editingProfile.temperature) updateData.temperature = formData.temperature;
      if (formData.max_tokens !== editingProfile.max_tokens) updateData.max_tokens = formData.max_tokens;
      if (formData.top_p !== editingProfile.top_p) updateData.top_p = formData.top_p;
      if (formData.base_url) updateData.base_url = formData.base_url;
      if (formData.api_key) updateData.api_key = formData.api_key;
      
      updateMutation.mutate(
        { id: editingProfile.id, data: updateData },
        {
          onSuccess: () => {
            setShowForm(false);
            resetForm();
          },
        }
      );
    } else {
      // Create new profile
      createMutation.mutate(formData, {
        onSuccess: () => {
          setShowForm(false);
          resetForm();
        },
      });
    }
  };
  
  const purposes: Record<string, string> = {
    translator: '翻译',
    classifier: '分类',
    digest: '摘要',
    planner: '规划',
    writer: '写作',
    search: '搜索',
    vision: '视觉',
  };
  
  return (
    <div className="space-y-6">
      <Tabs defaultValue="cards">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">LLM 配置</h3>
          <TabsList>
            <TabsTrigger value="cards">卡片视图</TabsTrigger>
            <TabsTrigger value="yaml" className="flex items-center gap-1">
              <FileCode className="h-3 w-3" />
              YAML
            </TabsTrigger>
          </TabsList>
        </div>
        
        {/* Unprocessed Items Card */}
        <Card className="mt-4">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                <div>
                  <h4 className="font-medium">待处理条目</h4>
                  {isLoadingCount ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {unprocessedCount?.total > 0 ? (
                        <>
                          {unprocessedCount.unprocessed_tweets} 条推文和 {unprocessedCount.unprocessed_rss} 篇 RSS 文章等待 AI 处理
                        </>
                      ) : (
                        '所有条目已处理完成'
                      )}
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={handleReprocess}
                disabled={reprocessMutation.isPending || !unprocessedCount?.total}
                size="sm"
                variant={unprocessedCount?.total > 0 ? "default" : "outline"}
              >
                {reprocessMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    处理中...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    重新处理
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
        
        <TabsContent value="cards" className="space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <Button onClick={handleOpenCreate} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              添加配置
            </Button>
          </div>
      
      {showForm && (
        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">用途</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2"
                    value={formData.purpose}
                    onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
                  >
                    {Object.entries(purposes).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">名称</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="配置名称"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Base URL</label>
                  <Input
                    value={formData.base_url}
                    onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                    placeholder="https://api.openai.com/v1"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">API Key</label>
                  <Input
                    type="password"
                    value={formData.api_key}
                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                    placeholder="sk-..."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">模型</label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="gpt-4o-mini"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Temperature</label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    value={formData.temperature}
                    onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      保存中...
                    </>
                  ) : (
                    editingProfile ? '更新' : '保存'
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={handleCancel}>
                  取消
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {profiles?.map((profile: LLMProfile) => (
          <Card key={profile.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{profile.name}</h4>
                    {profile.is_default && (
                      <Badge variant="default" className="text-xs">默认</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {purposes[profile.purpose] || profile.purpose}
                  </p>
                  <p className="text-sm text-muted-foreground">{profile.model}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenEdit(profile)}
                  >
                    <Pencil className="h-4 w-4 text-muted-foreground" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteMutation.mutate(profile.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
        </TabsContent>
        
        <TabsContent value="yaml" className="mt-4">
          <LLMProfilesYAML />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ManualProcessTab() {
  const manualRun = useManualRun();
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>手动处理</CardTitle>
          <CardDescription>
            立即触发一次处理运行，从上次处理时间到现在生成摘要和研究
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => manualRun.mutate()}
            disabled={manualRun.isPending}
            size="lg"
          >
            {manualRun.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                处理中...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                立即处理
              </>
            )}
          </Button>
          
          {manualRun.isSuccess && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-green-700 dark:text-green-300">
                处理完成！Run ID: {manualRun.data.run_id}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function SettingsPage() {
  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">设置</h2>
      
      <Tabs defaultValue="preferences">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="preferences">偏好设置</TabsTrigger>
          <TabsTrigger value="llm">LLM 配置</TabsTrigger>
          <TabsTrigger value="manual">手动处理</TabsTrigger>
        </TabsList>
        
        <TabsContent value="preferences" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <PreferencesTab />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="llm" className="mt-6">
          <Card>
            <CardContent className="p-6">
              <LLMProfilesTab />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="manual" className="mt-6">
          <ManualProcessTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
